import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type {
  AiTestConceptArtifact,
  AiTestConceptCombination,
  AiTestConceptTab,
  AiTestConceptTarget,
  AiTestConceptVisual,
} from './types';
import { listTesterConceptTargets } from './api';

export interface ConceptTargetSelection {
  blueprint: AiTestConceptTarget;
  tab: AiTestConceptTab;
  combination: AiTestConceptCombination;
  artifact: AiTestConceptArtifact | null;
  visual: AiTestConceptVisual | null;
}

interface ConceptTargetPickerProps {
  projectId: number | '';
  value: ConceptTargetSelection | null;
  onChange: (selection: ConceptTargetSelection | null) => void;
  requireArtifact?: boolean;
  requireVisual?: boolean;
  artifactLabel?: string;
  disabled?: boolean;
}

function preview(text: string, limit = 140): string {
  const trimmed = (text || '').trim().replace(/\s+/g, ' ');
  if (!trimmed) return '';
  return trimmed.length > limit ? `${trimmed.slice(0, limit)}…` : trimmed;
}

function conceptTabDisplayName(artifact: AiTestConceptArtifact, index: number): string {
  return artifact.name?.trim() || `Concept ${String(index + 1).padStart(2, '0')}`;
}

const ConceptTargetPicker: React.FC<ConceptTargetPickerProps> = ({
  projectId,
  value,
  onChange,
  requireArtifact,
  requireVisual,
  artifactLabel = 'Concept tab',
  disabled,
}) => {
  const [targets, setTargets] = useState<AiTestConceptTarget[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blueprintId, setBlueprintId] = useState<number | ''>('');
  const [tabId, setTabId] = useState<number | ''>('');
  const [combinationId, setCombinationId] = useState<number | ''>('');
  const [artifactId, setArtifactId] = useState<number | ''>('');
  const [visualId, setVisualId] = useState<number | ''>('');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!projectId) {
      setTargets([]);
      setBlueprintId('');
      setTabId('');
      setCombinationId('');
      setArtifactId('');
      setVisualId('');
      onChangeRef.current(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await listTesterConceptTargets(projectId);
        if (cancelled) return;
        setTargets(rows);
        const firstBlueprint = rows[0];
        const firstTab = firstBlueprint?.tabs[0];
        const firstCombo = firstTab?.combinations[0];
        const firstArtifact = firstCombo?.artifacts[0] || null;
        const firstVisual = firstArtifact?.visuals[0] || null;
        setBlueprintId(firstBlueprint?.id ?? '');
        setTabId(firstTab?.id ?? '');
        setCombinationId(firstCombo?.id ?? '');
        setArtifactId(firstArtifact?.id ?? '');
        setVisualId(firstVisual?.id ?? '');
        if (firstBlueprint && firstTab && firstCombo) {
          onChangeRef.current({
            blueprint: firstBlueprint,
            tab: firstTab,
            combination: firstCombo,
            artifact: firstArtifact,
            visual: firstVisual,
          });
        } else {
          onChangeRef.current(null);
        }
      } catch (err) {
        if (!cancelled) {
          setTargets([]);
          onChangeRef.current(null);
          setError(err instanceof Error ? err.message : 'Could not load concept targets');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const blueprint = targets.find((row) => row.id === blueprintId) || null;
  const tab = blueprint?.tabs.find((row) => row.id === tabId) || null;
  const combination = tab?.combinations.find((row) => row.id === combinationId) || null;
  const artifact = combination?.artifacts.find((row) => row.id === artifactId) || null;

  const emit = (
    nextBlueprint: AiTestConceptTarget | null,
    nextTab: AiTestConceptTab | null,
    nextCombo: AiTestConceptCombination | null,
    nextArtifact: AiTestConceptArtifact | null,
    nextVisual: AiTestConceptVisual | null,
  ) => {
    if (nextBlueprint && nextTab && nextCombo) {
      onChange({
        blueprint: nextBlueprint,
        tab: nextTab,
        combination: nextCombo,
        artifact: nextArtifact,
        visual: nextVisual,
      });
    } else {
      onChange(null);
    }
  };

  const missing = combination?.missing_attributes || [];
  const incomplete = Boolean(combination && !combination.attributes_complete);

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-[12px] font-medium text-[#626262] mb-1">
          Innovation platform
        </label>
        {!projectId ? (
          <p className="rounded-md border border-[#D1D1D1] px-3 py-2 text-sm text-[#8A8A8A]">
            Select a project first
          </p>
        ) : (
          <div
            role="listbox"
            aria-label="Innovation platform"
            className="w-full rounded-md border border-[#D1D1D1] bg-white"
          >
            {targets.map((row, index) => {
              const selected = row.id === blueprintId;
              const job = (row.job_to_be_done || '').trim();
              const tension = (row.tension || '').trim();
              const fallback = (row.name || '').trim() || 'Untitled innovation platform';
              return (
                <button
                  key={row.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={disabled || loading}
                  onClick={() => {
                    const nextTab = row.tabs[0] || null;
                    const nextCombo = nextTab?.combinations[0] || null;
                    const nextArtifact = nextCombo?.artifacts[0] || null;
                    const nextVisual = nextArtifact?.visuals[0] || null;
                    setBlueprintId(row.id);
                    setTabId(nextTab?.id ?? '');
                    setCombinationId(nextCombo?.id ?? '');
                    setArtifactId(nextArtifact?.id ?? '');
                    setVisualId(nextVisual?.id ?? '');
                    emit(row, nextTab, nextCombo, nextArtifact, nextVisual);
                  }}
                  className={`w-full text-left px-3 py-2.5 text-sm whitespace-normal break-words ${
                    index > 0 ? 'border-t border-[#E8E8E8]' : ''
                  } ${
                    selected
                      ? 'bg-[#E8F4FB] text-[#0C0B0B]'
                      : 'text-[#0C0B0B] hover:bg-[#F7F7F7]'
                  } disabled:opacity-50`}
                >
                  {job ? (
                    <span className="block">{job}</span>
                  ) : (
                    <span className="block">{fallback}</span>
                  )}
                  {tension && (
                    <span className="mt-1 block text-[13px] leading-snug text-[#626262]">
                      {tension}
                    </span>
                  )}
                  {row.market && (
                    <span className="mt-1 block text-[11px] text-[#8A8A8A]">{row.market}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <label className="block text-[12px] font-medium text-[#626262] mb-1">
          Recipe card tab
        </label>
        <select
          value={tabId}
          disabled={disabled || loading || !blueprint}
          onChange={(e) => {
            const nextId = e.target.value ? Number(e.target.value) : '';
            const nextTab = blueprint?.tabs.find((row) => row.id === nextId) || null;
            const nextCombo = nextTab?.combinations[0] || null;
            const nextArtifact = nextCombo?.artifacts[0] || null;
            const nextVisual = nextArtifact?.visuals[0] || null;
            setTabId(nextId);
            setCombinationId(nextCombo?.id ?? '');
            setArtifactId(nextArtifact?.id ?? '');
            setVisualId(nextVisual?.id ?? '');
            emit(blueprint, nextTab, nextCombo, nextArtifact, nextVisual);
          }}
          className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm disabled:bg-[#F5F5F5]"
        >
          {(!blueprint || blueprint.tabs.length === 0) && (
            <option value="">No recipe card tabs</option>
          )}
          {(blueprint?.tabs || []).map((row) => (
            <option key={row.id} value={row.id}>
              {row.name}
            </option>
          ))}
        </select>
        {tab && (
          <p className="mt-1 text-[12px] text-[#626262]">
            Brand: <span className="font-medium text-[#0C0B0B]">{tab.brand_name || '—'}</span>
            {' · '}
            Format: <span className="font-medium text-[#0C0B0B]">{tab.format_name || '—'}</span>
          </p>
        )}
      </div>

      <div>
        <label className="block text-[12px] font-medium text-[#626262] mb-1">
          Combination
        </label>
        <select
          value={combinationId}
          disabled={disabled || loading || !tab}
          onChange={(e) => {
            const nextId = e.target.value ? Number(e.target.value) : '';
            const nextCombo = tab?.combinations.find((row) => row.id === nextId) || null;
            const nextArtifact = nextCombo?.artifacts[0] || null;
            const nextVisual = nextArtifact?.visuals[0] || null;
            setCombinationId(nextId);
            setArtifactId(nextArtifact?.id ?? '');
            setVisualId(nextVisual?.id ?? '');
            emit(blueprint, tab, nextCombo, nextArtifact, nextVisual);
          }}
          className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm disabled:bg-[#F5F5F5]"
        >
          {(!tab || tab.combinations.length === 0) && (
            <option value="">No combinations on this card</option>
          )}
          {(tab?.combinations || []).map((row) => (
            <option key={row.id} value={row.id}>
              {row.name}
              {row.attributes_complete ? '' : ' (incomplete)'}
            </option>
          ))}
        </select>
      </div>

      {incomplete && (
        <p className="text-[12px] text-amber-700">
          Select an option for every attribute before generating. Missing:{' '}
          {missing.join(', ')}.
        </p>
      )}

      {requireArtifact && (
        <div>
          <label className="block text-[12px] font-medium text-[#626262] mb-1">
            {artifactLabel}
          </label>
          <select
            value={artifactId}
            disabled={disabled || loading || !combination}
            onChange={(e) => {
              const nextId = e.target.value ? Number(e.target.value) : '';
              const nextArtifact = combination?.artifacts.find((row) => row.id === nextId) || null;
              const nextVisual = nextArtifact?.visuals[0] || null;
              setArtifactId(nextId);
              setVisualId(nextVisual?.id ?? '');
              emit(blueprint, tab, combination, nextArtifact, nextVisual);
            }}
            className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm disabled:bg-[#F5F5F5]"
          >
            {(!combination || combination.artifacts.length === 0) && (
              <option value="">No concept tabs on this combination</option>
            )}
            {(combination?.artifacts || []).map((row, index) => (
              <option key={row.id} value={row.id}>
                {conceptTabDisplayName(row, index)}
                {(row.written_concept || '').trim() ? '' : ' (empty)'}
              </option>
            ))}
          </select>
          {artifact && preview(artifact.written_concept) && (
            <p className="mt-1 text-[12px] text-[#626262]">{preview(artifact.written_concept)}</p>
          )}
        </div>
      )}

      {requireVisual && (
        <div>
          <label className="block text-[12px] font-medium text-[#626262] mb-1">
            Visual concept
          </label>
          {(!artifact || artifact.visuals.length === 0) && !(artifact?.visual_concept || '').trim() ? (
            <p className="rounded-md border border-[#D1D1D1] px-3 py-2 text-sm text-[#8A8A8A]">
              This concept has no visuals yet.
            </p>
          ) : (
            <div className="w-full rounded-md border border-[#D1D1D1] bg-white">
              {(artifact?.visuals || []).map((row, index) => {
                const selected = row.id === visualId;
                return (
                  <button
                    key={row.id}
                    type="button"
                    disabled={disabled || loading}
                    onClick={() => {
                      setVisualId(row.id);
                      emit(blueprint, tab, combination, artifact, row);
                    }}
                    className={`flex w-full items-center gap-3 px-3 py-2 text-left ${
                      index > 0 ? 'border-t border-[#E8E8E8]' : ''
                    } ${selected ? 'bg-[#E8F4FB]' : 'hover:bg-[#F7F7F7]'} disabled:opacity-50`}
                  >
                    {row.image_url ? (
                      <img
                        src={row.image_url}
                        alt=""
                        className="h-12 w-12 rounded object-cover bg-[#F3F3F3]"
                      />
                    ) : (
                      <span className="h-12 w-12 rounded bg-[#F3F3F3]" />
                    )}
                    <span className="text-[12px] text-[#0C0B0B]">
                      Visual #{row.id}
                      {row.is_primary ? (
                        <span className="ml-1.5 text-[11px] text-[#8A8A8A]">Primary</span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-[12px] text-[#8A8A8A]">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading concept targets…
        </div>
      )}
      {error && <p className="text-[12px] text-red-600">{error}</p>}
      {!loading && projectId && targets.length === 0 && !error && (
        <p className="text-[12px] text-[#8A8A8A]">
          This project has no innovation platforms with recipe cards yet.
        </p>
      )}
      {value && requireArtifact && combination && combination.artifacts.length === 0 && (
        <p className="text-[12px] text-[#8A8A8A]">
          Add a concept tab on the Product idea view first.
        </p>
      )}
    </div>
  );
};

export default ConceptTargetPicker;
